import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import posthog from 'posthog-js';
import './FilterSidebar.css';
import { useTenant } from '../../context/ThemeContext';

import { RangeSlider } from '../RangeSlider';
import { CollapsibleSection } from '../CollapsibleSection';

import { BaseFacet } from '../facets/BaseFacet';
import { AlphabeticalFacet } from '../facets/AlphabeticalFacet';
import { CustomCountryFacet } from '../facets/CustomCountryFacet';
import { HierarchicalVariantFacet } from '../facets/HierarchicalVariantFacet';
import { HomologationFacet } from '../facets/HomologationFacet';

import { formatWithDot, roundToNearest10k } from '../../utils/helpers';
import { RangeState } from '../../types';
import { isHistoricMode } from '../../utils/apiConnector';

interface FilterSidebarProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setFilter: (field: string, value: any) => void;
  removeFilter: (field: string, value?: any) => void;
  filters?: any[];
  // Lifted range state
  yearRange: RangeState;
  setYearRange: (r: RangeState) => void;
  priceRange: RangeState;
  setPriceRange: (r: RangeState) => void;
  mileageRange: RangeState;
  setMileageRange: (r: RangeState) => void;
  horsePowerRange: RangeState;
  setHorsePowerRange: (r: RangeState) => void;
  // When true (mobile drawer), always show content — no internal toggle
  alwaysOpen?: boolean;
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  searchTerm,
  setSearchTerm,
  setFilter,
  removeFilter,
  filters = [],
  yearRange,
  setYearRange,
  priceRange,
  setPriceRange,
  mileageRange,
  setMileageRange,
  horsePowerRange,
  setHorsePowerRange,
  alwaysOpen = false,
}) => {
  const { tenant } = useTenant();
  const [showFilters, setShowFilters] = useState(false);
  const historicMode = isHistoricMode();

  const defaultYearRange = tenant.searchConfig?.yearRange || { min: 1989, max: 1999 };
  const defaultHorsePowerRange = tenant.searchConfig?.horsePowerRange || { min: 250, max: 450 };

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);

  useEffect(() => {
    const scrapedAtFilter = filters.find(f => f.field === 'scraped_at' && f.type === 'range');
    if (scrapedAtFilter && scrapedAtFilter.value) {
      const from = scrapedAtFilter.value.from ? scrapedAtFilter.value.from.split('T')[0] : '';
      const to = scrapedAtFilter.value.to ? scrapedAtFilter.value.to.split('T')[0] : '';
      setDateRange({ from, to });
      setIsDateFilterActive(true);
    }
  }, [filters]);

  const trackSearch = useCallback(
    debounce((term: string) => {
      posthog.capture('search_input', { search_term: term, brand: tenant.name });
    }, 1000),
    [tenant.name]
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      trackSearch(term);
    },
    [setSearchTerm, trackSearch]
  );

  const handleYearRangeChange = useCallback(([min, max]: [number, number]) => {
    setYearRange({ min, max });
  }, [setYearRange]);

  const handlePriceRangeChange = useCallback(([min, max]: [number, number]) => {
    setPriceRange({ min: roundToNearest10k(min), max: roundToNearest10k(max) });
  }, [setPriceRange]);

  const handleMileageRangeChange = useCallback(([min, max]: [number, number]) => {
    setMileageRange({ min: roundToNearest10k(min), max: roundToNearest10k(max) });
  }, [setMileageRange]);

  const handleHorsePowerRangeChange = useCallback(([min, max]: [number, number]) => {
    setHorsePowerRange({ min, max });
  }, [setHorsePowerRange]);

  const handleDateRangeChange = useCallback((field: string, range: { from: string; to: string }) => {
    setDateRange(range);
    if (range.from && range.to) {
      setFilter(field, { from: `${range.from}T00:00:00Z`, to: `${range.to}T23:59:59Z` });
      setIsDateFilterActive(true);
      posthog.capture('date_filter_used', { brand: tenant.name, field, from: range.from, to: range.to });
    } else {
      removeFilter(field);
      setIsDateFilterActive(false);
    }
  }, [setFilter, removeFilter, tenant.name]);

  const handleClearDateRange = useCallback(() => {
    removeFilter('scraped_at');
    setDateRange({ from: '', to: '' });
    setIsDateFilterActive(false);
  }, [removeFilter]);

  const formatPrice = useCallback((value: number, isMax = false) => {
    if (isMax && value === 250000) return '250.000\u20AC+';
    return `${formatWithDot(value)}${isMax ? '\u20AC' : ''}`;
  }, []);

  const formatMileage = useCallback((value: number, isMax = false) => {
    if (isMax && value === 250000) return '250.000km+';
    return `${formatWithDot(value)}${isMax ? 'km' : ''}`;
  }, []);

  const formatHorsePower = useCallback((value: number, isMax = false) => {
    if (isMax && value === defaultHorsePowerRange.max) return `${defaultHorsePowerRange.max}PS+`;
    return `${formatWithDot(value)}${isMax ? 'PS' : ''}`;
  }, [defaultHorsePowerRange.max]);

  const formatYear = useCallback((value: number) => value.toString(), []);

  const parseRangeValue = useCallback((value: string) => {
    return parseInt(value.replace(/[^\d]/g, '')) || 0;
  }, []);

  useEffect(() => {
    const priceIsDefault = priceRange.min === 0 && priceRange.max === 250000;
    const mileageIsDefault = mileageRange.min === 0 && mileageRange.max === 250000;
    const hpIsDefault =
      horsePowerRange.min === defaultHorsePowerRange.min &&
      horsePowerRange.max === defaultHorsePowerRange.max;
  
    const yearIsDefault =
      yearRange.min === defaultYearRange.min &&
      yearRange.max === defaultYearRange.max;
  
    if (!yearIsDefault) {
      setFilter('listing_production_year', {
        from: yearRange.min,
        to: yearRange.max + 1
      });
    }
  
    if (!priceIsDefault) {
      setFilter('listing_price_value', {
        from: priceRange.min,
        to: priceRange.max + 1
      });
    }
  
    if (!mileageIsDefault) {
      setFilter('listing_mileage_value', {
        from: mileageRange.min,
        to: mileageRange.max + 1
      });
    }
  
    if (!hpIsDefault) {
      setFilter('listing_ps', {
        from: horsePowerRange.min,
        to: horsePowerRange.max + 1
      });
    }
    if (priceIsDefault) removeFilter('listing_price_value');
if (mileageIsDefault) removeFilter('listing_mileage_value');
if (hpIsDefault) removeFilter('listing_ps');
if (yearIsDefault) removeFilter('listing_production_year');
  }, [yearRange, priceRange, mileageRange, horsePowerRange, setFilter]);

  const getTodayDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };
  const today = getTodayDate();

  // Content is visible if: desktop (lg:flex handles it), alwaysOpen, or showFilters toggled on
  const contentVisible = alwaysOpen || showFilters;

  return (
    <aside data-testid="filter-sidebar" className="w-full bg-mp-bg">
      <div className="px-6 pt-3 pb-6 flex flex-col gap-0">

        {/* Mobile toggle — hidden when alwaysOpen (inside drawer) */}
        {!alwaysOpen && (
          <button
            data-testid="mobile-filter-toggle"
            className="w-full lg:hidden flex justify-between items-center py-3 text-mp-text border border-mp-border px-4"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="text-sm font-display uppercase tracking-widest">Filters</span>
            <span className="material-symbols-outlined text-mp-muted">
              {showFilters ? 'expand_less' : 'expand_more'}
            </span>
          </button>
        )}

        <div
          data-testid="filter-content"
          className={`${contentVisible ? 'flex' : 'hidden'} lg:flex flex-col gap-0`}
        >
          {/* Historic mode banner */}
          {historicMode && (
            <div className="p-3 border border-mp-accent/30 bg-mp-accent/10">
              <p className="text-mp-accent font-mono text-xs uppercase">Historic Mode Active</p>
              <p className="text-mp-muted font-mono text-[10px] mt-1">Viewing all listings including expired/sold</p>
            </div>
          )}

          {/* Date range (historic only) */}
          {historicMode && (
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center border-t border-mp-border pt-3">
                <h3 className="font-display text-xs uppercase tracking-widest text-mp-muted">Extraction Date</h3>
                {isDateFilterActive && (
                  <button onClick={handleClearDateRange} className="text-primary font-mono text-[10px]">CLEAR</button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => handleDateRangeChange('scraped_at', { ...dateRange, from: e.target.value })}
                  max={dateRange.to || today}
                  className="flex-1 bg-mp-surface border border-mp-border text-mp-text font-mono text-xs px-2 py-1 text-center focus:outline-none focus:border-primary focus:ring-0"
                />
                <span className="text-mp-muted font-mono text-xs self-center">-</span>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => handleDateRangeChange('scraped_at', { ...dateRange, to: e.target.value })}
                  min={dateRange.from}
                  max={today}
                  className="flex-1 bg-mp-surface border border-mp-border text-mp-text font-mono text-xs px-2 py-1 text-center focus:outline-none focus:border-primary focus:ring-0"
                />
              </div>
            </div>
          )}

          {/* Generation */}
          <BaseFacet field="listing_model_generation_txt" label="Generation" show={5} otherAtBottom={true} sortType="count">
  {({ options, selectedValues, handleToggle }: any) => {
    const useDropdown = options.length > 3;
    return (
      <div className="flex items-center h-8 border border-mp-border bg-mp-surface px-3 mb-6">
        <span className="font-display text-xs uppercase tracking-widest text-mp-muted shrink-0">Generation</span>

        {useDropdown ? (
          <select
            className="ml-auto bg-transparent border-none text-mp-text font-mono text-[10px] uppercase tracking-widest cursor-pointer focus:outline-none focus:ring-0 text-right"
            value={selectedValues.size === 1 ? Array.from(selectedValues)[0] as string : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val) handleToggle(val);
            }}
          >
            <option value="" className="bg-mp-surface text-mp-muted">ALL</option>
            {options.map((option: any) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-mp-surface text-mp-text"
              >
                {option.value}
              </option>
            ))}
          </select>
        ) : (
          <div className="flex items-center gap-3 ml-auto">
            {options.map((option: any) => {
              const isActive = selectedValues.has(option.value) || selectedValues.size === 0;
              return (
                <div
                  key={option.value}
                  className={`cursor-pointer font-mono text-[10px] hover:text-mp-text transition-colors ${
                    isActive ? 'text-mp-text' : 'text-mp-muted'
                  }`}
                  onClick={() => handleToggle(option.value)}
                >
                  {option.value}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }}
</BaseFacet>

          {/* Variant */}
          <HierarchicalVariantFacet />

          {/* Range Sliders */}
          <RangeSlider label="Price" min={0} max={250000} step={10000} value={[priceRange.min, priceRange.max]} onChange={handlePriceRangeChange} formatValue={formatPrice} parseValue={parseRangeValue} />
          <RangeSlider label="Horsepower" min={defaultHorsePowerRange.min} max={defaultHorsePowerRange.max} value={[horsePowerRange.min, horsePowerRange.max]} onChange={handleHorsePowerRangeChange} formatValue={formatHorsePower} parseValue={parseRangeValue} />
          <RangeSlider label="Year" min={defaultYearRange.min} max={defaultYearRange.max} value={[yearRange.min, yearRange.max]} onChange={handleYearRangeChange} formatValue={formatYear} parseValue={parseRangeValue} />
          <RangeSlider label="Mileage" min={0} max={250000} step={10000} value={[mileageRange.min, mileageRange.max]} onChange={handleMileageRangeChange} formatValue={formatMileage} parseValue={parseRangeValue} />

          {/* Facets */}
          <CollapsibleSection title="Body Style">
            <BaseFacet field="listing_body_type_txt" label="" show={5} otherAtBottom={true} sortType="count">
              {({ options, selectedValues, handleToggle }: any) => (
                <div className="flex items-center flex-wrap gap-4 pt-1 pb-1">
                  {options.map((option: any) => {
                    const selectedArray = Array.from(selectedValues);
                    const hasSelection = selectedArray.length > 0;
                    const isActive = selectedArray.includes(option.value) || !hasSelection;
                    return (
                      <button key={option.value} onClick={() => handleToggle(option.value)}
                        className={`font-mono text-xs transition-colors ${isActive ? 'text-mp-text' : 'text-mp-muted'}`}>
                        {option.value}
                      </button>
                    );
                  })}
                </div>
              )}
            </BaseFacet>
          </CollapsibleSection>

          <CollapsibleSection title="Build">
            <AlphabeticalFacet field="listing_build" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Transmission">
            <BaseFacet field="listing_transmission_txt" label="" show={30} otherAtBottom={true} sortType="count">
              {({ options, selectedValues, handleToggle }: any) => (
                <div className="flex items-center flex-wrap gap-4 pt-1 pb-1">
                  {options.map((option: any) => {
                    const isActive = selectedValues.has(option.value) || selectedValues.size === 0;
                    return (
                      <button key={option.value} onClick={() => handleToggle(option.value)}
                        className={`font-mono text-xs transition-colors ${isActive ? 'text-mp-text' : 'text-mp-muted'}`}>
                        {option.value}
                      </button>
                    );
                  })}
                </div>
              )}
            </BaseFacet>
          </CollapsibleSection>

          <CollapsibleSection title="Base Color">
            <AlphabeticalFacet field="listing_grouped_base_color" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Detailed Color">
            <AlphabeticalFacet field="listing_grouped_exterior_color" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Interior Color">
            <AlphabeticalFacet field="listing_interior_base_color" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Detailed Color (Full Text)">
            <AlphabeticalFacet field="listing_exterior_color_txt" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Interior Color (Full Text)">
            <AlphabeticalFacet field="listing_interior_color_txt" label="" show={30} otherAtBottom={true} />
          </CollapsibleSection>

          <CollapsibleSection title="Registration">
            <BaseFacet field="listing_road_legal" label="" sortType="count">
              {({ options, selectedValues, handleToggle }: any) => (
                <div className="flex items-center pt-1 pb-1">
                  <span className="font-display text-[10px] uppercase tracking-widest text-mp-muted">Road Legal</span>
                  <div className="flex items-center gap-4 ml-auto">
                    {options.map((option: any) => {
                      const isActive = selectedValues.has(option.value) || selectedValues.size === 0;
                      const displayValue = option.value.toLowerCase() === 'unknown' ? 'Other' : option.value;
                      return (
                        <button key={option.value} onClick={() => handleToggle(option.value)}
                          className={`font-mono text-xs transition-colors ${isActive ? 'text-mp-text' : 'text-mp-muted'}`}>
                          {displayValue}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </BaseFacet>
            <BaseFacet field="listing_classic_car" label="" sortType="count">
              {({ options, selectedValues, handleToggle }: any) => (
                <div className="flex items-center pt-1 pb-1">
                  <span className="font-display text-[10px] uppercase tracking-widest text-mp-muted">Classic Car (H)</span>
                  <div className="flex items-center gap-4 ml-auto">
                    {options.map((option: any) => {
                      const isActive = selectedValues.has(option.value) || selectedValues.size === 0;
                      const displayValue = option.value.toLowerCase() === 'unknown' ? 'Other' : option.value;
                      return (
                        <button key={option.value} onClick={() => handleToggle(option.value)}
                          className={`font-mono text-xs transition-colors ${isActive ? 'text-mp-text' : 'text-mp-muted'}`}>
                          {displayValue}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </BaseFacet>
          </CollapsibleSection>

          {tenant.id === 'porsche' && (
            <CollapsibleSection title="Homologation">
              <HomologationFacet />
            </CollapsibleSection>
          )}

          {tenant.id === 'mercedes' && (
            <CollapsibleSection title="Engine Type">
              <BaseFacet field="listing_engine_type" label="" show={5} otherAtBottom={false} sortType="count">
                {({ options, selectedValues, handleToggle }: any) => (
                  <div className="flex items-center flex-wrap gap-4 pt-1 pb-1">
                    {options.map((option: any) => {
                      const isActive = selectedValues.has(option.value) || selectedValues.size === 0;
                      return (
                        <button key={option.value} onClick={() => handleToggle(option.value)}
                          className={`font-mono text-xs transition-colors ${isActive ? 'text-mp-text' : 'text-mp-muted'}`}>
                          {option.value}
                        </button>
                      );
                    })}
                  </div>
                )}
              </BaseFacet>
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Listing Sold">
            <BaseFacet field="listing_already_sold" label="" sortType="count" transformOptions={(options: any[]) => {
              const transformed: any[] = [];
              options.forEach((option: any) => {
                const value = option.value.toString().toLowerCase() === 'no' ? 'unknown' : option.value.toString();
                const existing = transformed.find((opt: any) => opt.value === value);
                if (existing) { existing.count += option.count || 0; }
                else { transformed.push({ value, count: option.count || 0 }); }
              });
              return transformed.filter((opt: any) => opt.value.toLowerCase() === 'yes' || opt.value.toLowerCase() === 'unknown').sort((a: any, b: any) => b.count - a.count);
            }}>
              {({ options, selectedValues, handleToggle, driver }: any) => (
                <div className="flex items-center flex-wrap gap-4 pt-1 pb-1">
                  {options.map((option: any) => {
                    const isSelected = selectedValues.has(option.value);
                    const displayValue = option.value.toLowerCase() === 'unknown' ? 'Other' : option.value;
                    const handleClick = () => {
                      if (option.value.toLowerCase() === 'unknown') {
                        if (isSelected) { driver.removeFilter('listing_already_sold', 'unknown'); driver.removeFilter('listing_already_sold', 'no'); }
                        else { driver.addFilter('listing_already_sold', 'unknown', 'any'); driver.addFilter('listing_already_sold', 'no', 'any'); }
                      } else { handleToggle(option.value); }
                    };
                    return (
                      <button key={option.value} onClick={handleClick}
                        className={`font-mono text-xs transition-colors ${isSelected ? 'text-mp-text' : 'text-mp-muted/40 hover:text-mp-muted'}`}>
                        {displayValue}
                      </button>
                    );
                  })}
                </div>
              )}
            </BaseFacet>
          </CollapsibleSection>

          <CollapsibleSection title="Expert Appraisal">
            <BaseFacet field="listing_expert_appraisal" label="" sortType="count" transformOptions={(options: any[]) => {
              const transformed: any[] = [];
              options.forEach((option: any) => {
                const value = option.value.toString().toLowerCase() === 'no' ? 'unknown' : option.value.toString();
                const existing = transformed.find((opt: any) => opt.value === value);
                if (existing) { existing.count += option.count || 0; }
                else { transformed.push({ value, count: option.count || 0 }); }
              });
              return transformed.filter((opt: any) => opt.value.toLowerCase() === 'yes' || opt.value.toLowerCase() === 'unknown').sort((a: any, b: any) => b.count - a.count);
            }}>
              {({ options, selectedValues, handleToggle, driver }: any) => (
                <div className="flex items-center flex-wrap gap-4 pt-1 pb-1">
                  {options.map((option: any) => {
                    const isSelected = selectedValues.has(option.value);
                    const displayValue = option.value.toLowerCase() === 'unknown' ? 'Other' : option.value;
                    const handleClick = () => {
                      if (option.value.toLowerCase() === 'unknown') {
                        if (isSelected) { driver.removeFilter('listing_expert_appraisal', 'unknown'); driver.removeFilter('listing_expert_appraisal', 'no'); }
                        else { driver.addFilter('listing_expert_appraisal', 'unknown', 'any'); driver.addFilter('listing_expert_appraisal', 'no', 'any'); }
                      } else { handleToggle(option.value); }
                    };
                    return (
                      <button key={option.value} onClick={handleClick}
                        className={`font-mono text-xs transition-colors ${isSelected ? 'text-mp-text' : 'text-mp-muted/40 hover:text-mp-muted'}`}>
                        {displayValue}
                      </button>
                    );
                  })}
                </div>
              )}
            </BaseFacet>
          </CollapsibleSection>

          <CollapsibleSection title="Country">
            <CustomCountryFacet show={30} />
          </CollapsibleSection>

          <div className="border-t border-mp-border"></div>
        </div>
      </div>
    </aside>
  );
};
